import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { getBillingStatus, confirmBilling, me as fetchMe } from "../lib/api";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { setUser } = useAuth();
  const [state, setState] = useState("polling"); // polling | success | pending | error
  const [message, setMessage] = useState("Confirming your payment…");
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sid) {
      setState("error");
      setMessage("Missing session id.");
      return;
    }
    attemptsRef.current += 1;
    try {
      // First attempt: explicit confirmation. Real Stripe will verify;
      // Emergent test-mode proxy will fall back to trusting the redirect.
      if (attemptsRef.current === 1) {
        try {
          await confirmBilling(sid);
        } catch (_) {
          // ignore — fall through to status poll
        }
      }
      const data = await getBillingStatus(sid);
      if (data.payment_status === "paid" && data.applied) {
        setState("success");
        setMessage("Payment received. You're on Solo for the next 30 days.");
        try {
          const me = await fetchMe();
          setUser(me);
        } catch (_) {}
        return;
      }
      if (data.status === "expired") {
        setState("error");
        setMessage("This checkout session expired before payment completed.");
        return;
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("pending");
        setMessage(
          "Still waiting on Stripe to confirm. We'll keep checking — refresh in a minute."
        );
        return;
      }
      setMessage("Payment is being processed…");
      setTimeout(poll, POLL_MS);
    } catch (e) {
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("error");
        setMessage(e.message || "Couldn't confirm payment.");
        return;
      }
      setTimeout(poll, POLL_MS);
    }
  }, [sid, setUser]);

  useEffect(() => {
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tone =
    state === "success"
      ? { border: "border-emerald-200", bg: "bg-emerald-50", icon: <CheckCircle2 className="w-7 h-7 text-emerald-600" /> }
      : state === "error"
      ? { border: "border-red-200", bg: "bg-red-50", icon: <AlertTriangle className="w-7 h-7 text-red-600" /> }
      : { border: "border-slate-200", bg: "bg-white", icon: <Loader2 className="w-7 h-7 animate-spin text-emerald-600" /> };

  return (
    <div data-testid="billing-success-page" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
      <div className={`max-w-md w-full rounded-3xl border ${tone.border} ${tone.bg} p-8 shadow-soft text-center`}>
        <div className="mx-auto w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-inner">
          {tone.icon}
        </div>
        <h1 data-testid="billing-success-title" className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
          {state === "success" ? "You're on Solo" : state === "error" ? "Payment problem" : "Confirming payment"}
        </h1>
        <p data-testid="billing-success-message" className="mt-2 font-figtree text-sm text-[#475569] leading-relaxed">
          {message}
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button
            data-testid="billing-success-app-btn"
            onClick={() => navigate("/app")}
            className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6 py-5"
          >
            Continue to app
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            data-testid="billing-success-account-btn"
            onClick={() => navigate("/account")}
            variant="outline"
            className="rounded-full border-slate-200 hover:bg-slate-50 font-figtree text-sm"
          >
            View account
          </Button>
        </div>
      </div>
    </div>
  );
}
